import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { Location } from "@angular/common";
import { CoreLogService } from "../../services/log.service";
import { Router, ActivatedRoute } from "@angular/router";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { CoreLanguageService } from "../../services/language.service";
import { FormControl, Validators } from "@angular/forms";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { Subscription } from "rxjs/Subscription";
import { UserHelperService } from "../../services/userHelper.service";
import { UserProfileService } from "../userProfile/userprofile.service";
import { IOSHelperService } from "../../services/IOSHelper.service";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { WarningdialogComponent } from "../../modalDialogs";
import Utils from "../../shared/utils";
import { LocationService } from "../location/location.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { RemoteOrderGroupEntryService } from "./remoteOrderGroupEntry.service";
import { IRemoteGroupCodeInfo, EntryGroupResult, RemoteGroupMember } from "./remoteOrderGroupEntry.facade";
import { orderTypes, SnackBarMessageType } from "../../shared/contentTypes";
import { RemoteGroupOrderService } from "../../services/RemoteGroupOrder.service";
import { snackBarMessageKey, SnackBarService } from "../../services/snackBar.service";
import { forbiddenNameValidator, ValidatorsConfig } from "../../shared/validator/validator.function";
import { WebShopService } from "../../services/webShop.service";
import { FieldName, IErrorMessage, ValidationErrorType } from "../../shared/validator/validatorMessage.interface";
import { REMOTE_GROUPORDER_NAME_ERROR_MESSAGES } from "../../shared/validator/validatorErrorMessages";

/**
 * Initialization: Retrieve route data and check for a valid group ID. 
 * If not found, log an error and navigate back to the home page.
 * Handle Group Entry: Check if the user is already a member of the group. 
 * If true, show a warning-dialog. If not, proceed to process group information.
 * 
 * Process Group Information: Retrieve and validate group information. If valid and not full,
 * verify and set group details, then check the location and attempt to reassign the user.
 * If the group is full or invalid, show appropriate warnings.
 * 
 * Join Group Order: Attempt to join the group with the provided user details. 
 * Handle the result by showing success, warnings, or errors based on the outcome.
 * 
 * Deny Group Order: Navigate back to the home page if the user denies the group order.
 */

@Component({
  selector: "remoteOrderGroupEntry",
  templateUrl: "./remoteOrderGroupEntry.component.html",
  styleUrls: ["./remoteOrderGroupEntry.component.scss"],
  providers: [OverlaySpinnerService, RemoteOrderGroupEntryService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class RemoteOrderGroupEntryComponent implements OnInit, OnDestroy {

  private _langSubscriber: Subscription;
  private _userNameInputSubscriber: Subscription;
  private _validationSubscriber: Subscription;

  // parameter
  private locationID: number = 0;
  private remote_groupId: number = null;
  private remote_group_name: string = "";
  private remote_group_creator: string = "";

  private userName: string;
  public isSmallView: boolean = false;
  public field = FieldName;

  public usernameInputControl = new FormControl('', {
    validators: ValidatorsConfig.forGroupMemberName(),
    asyncValidators: forbiddenNameValidator(this._webShopService),
  });

  constructor(private _userHelperService: UserHelperService
    , private _location: Location
    , private _webShopService: WebShopService
    , private _remoteGroupOrderEntryService: RemoteOrderGroupEntryService
    , private _remoteGroupOrderService: RemoteGroupOrderService
    , private _userProfileService: UserProfileService
    , private _router: Router
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _mainnavService: MainnavService
    , private internationalisationService: InternationalisationService
    , private _iosHelperService: IOSHelperService
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _activatedRoute: ActivatedRoute
    , private _locationService: LocationService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _snackBarService: SnackBarService
  ) {
    this.isSmallView = this._mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("RemoteOrderGroupEntryComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    const parts = location.hash.split("/");
    const lastPart = parts[parts.length - 1];
    const groupCode = lastPart.split("=")[1];
    // currently groupCode is the id
    const groupId = parseInt(groupCode);
    me.remote_groupId = groupId;

    if (!me.remote_groupId) {
      me._coreLogService.error("RemoteOrderGroupEntryComponent.ngOnInit", "no group_code");
      me._router.navigate(["/"]);
      return;
    }
    me.handleGroupEntry();
    me._langSubscriber = me.internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me._validationSubscriber = me.usernameInputControl.statusChanges.subscribe(() => {
      me.PushChanges();
    });

    me._userNameInputSubscriber = me.usernameInputControl.valueChanges.subscribe(async () => {
      me.usernameInputControl.markAsTouched();
      me.userName = me.usernameInputControl.value;
      me.PushChanges();
    });

    me.InitUserProfileName();
    me.PushChanges();
  }

  /** Initializes the user's profile name if the user is registered.*/
  private async InitUserProfileName(): Promise<void> {
    const me = this;
    if (me._userHelperService.IsRegistered) {
      try {
        const userViewModel = await me._userProfileService.GetUserInfoViewModel();
        me.userName = userViewModel.fname;
        me.usernameInputControl.setValue(me.userName);
        me.PushChanges();
      } catch (error) {
        me._coreLogService.error("GroupOrderInviteComponent.GetUserProfileName", "GetUserInfoViewModel", error);
      }
    }
  }

  /** Get active error message for a form control based on field type */
  public activeError(control: FormControl, fieldName: FieldName): string | null {
    return ValidatorsConfig.getActiveErrorMessage(control, REMOTE_GROUPORDER_NAME_ERROR_MESSAGES, fieldName);
  }

  /**  Getter for the welcome label, replacing placeholders with group and user names.*/
  public get welcomeLabel(): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Entry_Title")
      .replace("{groupName}", `<br><span class="emphasized">${me.remote_group_name}</span><br>`)
      .replace("{userName}", `<span class="emphasized">${me.remote_group_creator}</span>`);
  }

  /** Getter for the input placeholder text.*/
  public get inputPlaceholder(): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Entry_input");
  }

  /** Sets the button state based on the validity of the username input.*/
  public setBtnStateByUserName(): boolean {
    const me = this;
    return !Utils.IsNullOrEmpty(me.usernameInputControl.value) && me.usernameInputControl.valid ? true : false;
  }

  /** Sets the web view for iOS after blur.*/
  public setWebView(): void {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  /** Navigates back to the home page.*/
  public denyGroupOrder(): void {
    const me = this;
    me._router.navigate(["/"]); // back to home
  }

  /** Handles the group entry process by checking membership status and processing group information. */
  private async handleGroupEntry() {
    const me = this;
    try {
      const membershipStatus = await me._remoteGroupOrderEntryService.membershipStatus(me.remote_groupId);
      if (membershipStatus === RemoteGroupMember.Yes) {
        me._router.navigate(["/remoteOrderGroupServiceSelector"]);
      } else if (membershipStatus === RemoteGroupMember.Not) {
        await me.processGroupInfo();
      } else {
        me._router.navigate(["/menuservice"]);
        me.handleError("handleGroupEntry membershipStatus unknown", RemoteGroupMember.Unknown);
      }
    } catch (error) {
      me.handleError("handleGroupEntry.catch", error);
    }
    me.PushChanges();
  }

  /** Processes the group information and verifies its validity and capacity. */
  private async processGroupInfo() {
    const me = this;
    try {
      const info = await me._remoteGroupOrderEntryService.getGroupInfo(me.remote_groupId);
      if (info.valid && !info.isFull) {
        me.verifyAndSetGroupInfo(info);
        // TODO: uncomment this code, as soon as it has been fixed
        // await me.checkLocationAndReassign(info.groupInfo.orderType);
        me.PushChanges();
      } else if (info.isFull) {
        me.OpenWarningMaxCountReached();
      } else {
        me.OpenWarningLinkExpired();
      }
    } catch (error) {
      me.handleError("getGroupCodeInfo.catch", error);
    }
  }

  /** Verifies the group information and sets the relevant properties. */
  private verifyAndSetGroupInfo(info: IRemoteGroupCodeInfo) {
    const me = this;
    if (me.remote_groupId > 0) {
      me.locationID = info.location;
      me.remote_group_name = info.groupInfo.groupName;
      me.remote_group_creator = info.groupInfo.groupCreator;
      const equal = me.remote_groupId === info.groupInfo.groupId;
      if (!equal) {
        me.handleError("Params and GroupCodeInfo not equal", {
          groupId: me.remote_groupId,
        });
      }
    }
  }

  //TODO ordertype / tryReassignGuestToPreviousGroup
  /** Checks the location's open time and attempts to reassign the guest to their previous group. */
  private async checkLocationAndReassign(orderType: number) {
    const me = this;
    // TODO: adjust the underlying functions/db so that this works without this
    const orderTypeToUseForCheckingOpen = orderType === orderTypes.deliveryGroup ? orderTypes.delivery
      : orderType === orderTypes.pickupGroup ? orderTypes.pickup
        : orderType;
    try {
      const locationInfo = await me._locationService.GetWebshopLocationInfoViewModel(orderTypeToUseForCheckingOpen, me.locationID);
      if (locationInfo && locationInfo.checkOpenTime()) {
        me.PushChanges();
        me._overlaySpinnerService.ShowLayoutSpinner();
        const canReassign = await me._remoteGroupOrderEntryService.tryReassignGuestToPreviousGroup(me.remote_groupId);
        if (canReassign) {
          me._router.navigate(["/remoteOrderGroupServiceSelector"]);
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      } else {
        me.OpenWarningLocationClosed();
      }
    } catch (error) {
      me.handleError("tryReassignGuestToPreviousGroup.catch", error);
    }
  }

  /** Attempts to join the group order with the provided user details. */
  public async joinGroupOrder(): Promise<void> {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    try {
      const joinResult = await me._remoteGroupOrderEntryService.joinGroup(me.userName, me.locationID, me.remote_group_name, me.remote_groupId);
      me.handleJoinResult(joinResult);
      me._remoteGroupOrderService.startOrderTimer(joinResult === EntryGroupResult.Okay);
    } catch (error) {
      me.handleError("joinGroupOrder.catch", error);
    } finally {
      me._overlaySpinnerService.HideLayoutSpinner();
    }
    me.PushChanges()
  }

  /** Handles the result of the join group order attempt. */
  private handleJoinResult(joinResult: EntryGroupResult) {
    const me = this;
    switch (joinResult) {
      case EntryGroupResult.Okay:
        // Joining the group was successful, automatic redirection will occur
        break;
      case EntryGroupResult.WrongUserName:
        me.OpenWarningCreateUserNameFailed();
        break;
      case EntryGroupResult.GroupFull:
        me.OpenWarningMaxCountReached();
        break;
      case EntryGroupResult.UserIsInGroup:
        // The user is already in the group, this call should not have occurred
        break;
      case EntryGroupResult.Fail:
        me.OpenWarningLinkExpired();
        break;
      case EntryGroupResult.PlaceUndefined:
        me._snackBarService.ShowErrorMessageFailed(128);
        break;
      default:
        me.handleError("unknown joinResult", joinResult);
        break;
    }
  }

  /** Handles errors by logging them and opening a warning. */
  private handleError(message: string, error?: any) {
    const me = this;
    me._coreLogService.error("RemoteOrderGroupEntryComponent", message, error);
  }

  /** Warning dialog: Der Standort ist momentan geschlossen  */
  private OpenWarningLocationClosed() {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phGroupOrder_LocationClosed_Title"
        , content: "phGroupOrder_LocationClosed_SubText"
        , btnLabel: "phGlobal_Continue"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    warningButtomSheet.afterDismissed().subscribe((result) => {
      me._router.navigate(["/menuservice"]);
    });
  }

  /** Warning dialog: Max Count reached  */
  private OpenWarningMaxCountReached() {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phGroupOrder_MaxMemberCount_Title"
        , content: "phGroupOrder_MaxMemberCount_SubText"
        , btnLabel: "phGlobal_Continue"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    warningButtomSheet.afterDismissed().subscribe((result) => {
      me._router.navigate(["/menuservice"]);
    });
  }

  /** Warning dialog:Group expired  */
  private OpenWarningLinkExpired() {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phGroupOrder_LinkExpired_Title"
        , content: "phGroupOrder_LinkExpired_SubText"
        , btnLabel: "phGroupOrder_LinkExpired_Btn"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    warningButtomSheet.afterDismissed().subscribe((result) => {
      me._router.navigate(["/menuservice"]);
    });
  }

  /** Warning dialog: user name  */
  private OpenWarningCreateUserNameFailed() {
    const me = this;
    me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phGroupOrder_CreateGroup_UserName_Failed_Title"
        , content: "phGroupOrder_CreateGroup_Name_Failed_SubText"
        , btnLabel: "phGlobal_Return"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
  }

  /** navigate back on swipe from left to right */
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }
  /** navigate back */
  public navigateBack(): void {
    this._location.back();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._userNameInputSubscriber) {
      me._userNameInputSubscriber.unsubscribe();
    }
    if (me._validationSubscriber) {
      me._validationSubscriber.unsubscribe();
    }
  }
}
